import { useMemo, useContext } from "react";
import { useMutation } from "@apollo/client";
import type { AgGridReact } from "ag-grid-react";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { getLoggedinUserId } from "services/auth";
import { MUTATION_SAVE_COLUMNS } from "services/graphQL/mutations";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import type { TSaveColumnState } from "components/column-config-views";
import type { ConditionalFormattingRule } from "components/conditional-formatting";
import { message } from "utils/ciq-message";
import { QUERY_GET_DESIGN_SCOPE_COLUMN_CONFIG } from "../queries";
import {
  DEFAULT_DESIGN_SCOPE_VIEW_LAYOUT,
  type DesignScopeViewLayout,
  withDesignScopeViewMeta
} from "../shared/design-scope-view-meta";

export function useDesignScopeColumnConfig(props: {
  gridRef: React.RefObject<AgGridReact | null>;
  getConditionalsFormatting?: () => ConditionalFormattingRule[];
  /** Scope list search lives in React state, not AG Grid quickFilterText. */
  getQuickSearch?: () => string;
  /** List/Hierarchy (+ UF/MF) selection — stored as a synthetic columnList row. */
  getViewLayout?: () => DesignScopeViewLayout;
}) {
  const { gridRef, getConditionalsFormatting, getQuickSearch, getViewLayout } =
    props;
  const { gqlClientForProject, isProjectArchived } = useContext(ProjectContext);
  const loggedinUserId = useMemo(() => getLoggedinUserId(), []);

  const { data: savedColumnsData, loading: userColumnConfigLoading } =
    useCIQQuery<{
      project_users: Array<{ design_scope_column_config: TSaveColumnState }>;
    }>(QUERY_GET_DESIGN_SCOPE_COLUMN_CONFIG, {
      fetchPolicy: "no-cache",
      client: gqlClientForProject,
      skip: !gqlClientForProject || !loggedinUserId,
      variables: {
        where: { user_id: { _eq: loggedinUserId } }
      }
    });

  const isColumnConfigReady =
    !gqlClientForProject || !loggedinUserId || !userColumnConfigLoading;

  const savedColumns = useMemo(() => {
    if (!savedColumnsData?.project_users?.length) return null;
    const resArr = savedColumnsData.project_users;
    return resArr[resArr.length - 1];
  }, [savedColumnsData]);

  const [saveColumnsStateMutation] = useMutation(MUTATION_SAVE_COLUMNS, {
    client: gqlClientForProject
  });

  const onSaveColumnState = async () => {
    const api = gridRef.current?.api;
    if (isProjectArchived || !api || api.isDestroyed?.()) return;
    const columnsStates: TSaveColumnState = withDesignScopeViewMeta(
      {
        ...UtilColumnConfig.getColumnStateFromGrid({
          gridRef: {
            api
          },
          isIncludeSearchText: false,
          conditionals_formatting: getConditionalsFormatting?.() ?? []
        }),
        quickSearch: getQuickSearch?.() ?? ""
      },
      getViewLayout?.() ?? DEFAULT_DESIGN_SCOPE_VIEW_LAYOUT
    );

    try {
      const updateResponse: any = await saveColumnsStateMutation({
        variables: {
          design_scope_column_config: columnsStates
        }
      });
      if (updateResponse.errors) {
        message.error({ content: updateResponse.errors[0].message });
      }
    } catch {
      console.error("An error occured");
    }
  };

  return {
    savedColumns,
    isColumnConfigReady,
    onSaveColumnState
  };
}
